import type { ReactNode } from "react";
import type { Control } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  MBC_BRIEF_FIELD_LABELS,
  MBC_JUDGED_BRIEF_FIELDS,
  type MbcAdminCheck,
  type MbcAdminCreateCheckBody,
  mbcAdminCreateCheckBodyZod,
  type MbcJudgedBriefField,
} from "@nlc/shared-types";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import {
  Description,
  FieldError,
  Label,
  TextArea,
  TextField,
} from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { toast } from "@/components/toast/toast-queue";

import { useCreateCheck } from "./hooks/useCreateCheck";
import { useUpdateCheck } from "./hooks/useUpdateCheck";

const FIELD_OPTIONS = MBC_JUDGED_BRIEF_FIELDS.map((id) => ({
  id: id as MbcJudgedBriefField,
  label: MBC_BRIEF_FIELD_LABELS[id],
}));

const EMPTY_CHECK: MbcAdminCreateCheckBody = {
  askWhenFailing: "",
  field: "visualDirection",
  label: "",
  multipleVariationsOnly: false,
  passesWhen: "",
};

interface CheckFormProps {
  readonly check: MbcAdminCheck | null;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
  readonly sidebar?: ReactNode;
}

export function CheckForm({
  check,
  onCancel,
  onSaved,
  sidebar,
}: CheckFormProps) {
  const createCheck = useCreateCheck();
  const updateCheck = useUpdateCheck();
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<MbcAdminCreateCheckBody>({
    defaultValues: check ?? EMPTY_CHECK,
    resolver: zodResolver(mbcAdminCreateCheckBodyZod),
  });

  const onSubmit = async (values: MbcAdminCreateCheckBody) => {
    try {
      await (check
        ? updateCheck.mutateAsync({
            askWhenFailing: values.askWhenFailing,
            checkId: check._id,
            label: values.label,
            multipleVariationsOnly: values.multipleVariationsOnly,
            passesWhen: values.passesWhen,
          })
        : createCheck.mutateAsync(values));
      toast.add(
        {
          description: "The check has been saved.",
          title: "Check saved",
          type: "success",
        },
        { timeout: 5000 },
      );
      onSaved();
    } catch (error) {
      toast.add({
        description: (error as Error).message,
        title: "Could not save the check",
        type: "error",
      });
    }
  };

  return (
    <form
      className="rounded-b-xl bg-bg-primary"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="grid items-start gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <section className="flex flex-col gap-6">
            <h2 className="text-md font-semibold text-text-primary">Check</h2>

            <Controller
              control={control}
              name="field"
              render={({ field }) => (
                <Select
                  isDisabled={check !== null}
                  onSelectionChange={(key) =>
                    field.onChange(key as MbcJudgedBriefField)
                  }
                  selectedKey={field.value}
                >
                  <Label>Brief field</Label>
                  <SelectButton />
                  <SelectPopover>
                    <SelectListBox items={FIELD_OPTIONS}>
                      {(item) => (
                        <SelectListItem id={item.id} textValue={item.label}>
                          {item.label}
                        </SelectListItem>
                      )}
                    </SelectListBox>
                  </SelectPopover>
                </Select>
              )}
            />

            <CheckTextArea
              control={control}
              description="Shown to the client next to the check."
              label="Label"
              name="label"
              rows={1}
            />

            <CheckTextArea
              control={control}
              description="The condition the judge verifies. Changing it re-judges this check on the next evaluation."
              label="Passes when"
              name="passesWhen"
              rows={4}
            />

            <CheckTextArea
              control={control}
              description="The suggestion shown to the client when the check fails."
              label="Ask when failing"
              name="askWhenFailing"
              rows={3}
            />

            <Controller
              control={control}
              name="multipleVariationsOnly"
              render={({ field }) => (
                <Checkbox isSelected={field.value} onChange={field.onChange}>
                  Only with multiple variations
                </Checkbox>
              )}
            />
          </section>
        </div>

        {sidebar}
      </div>

      <div className="flex justify-end gap-5 border-t border-border-primary px-6 py-4 text-text-primary">
        <Button onPress={onCancel} variant="plain">
          Cancel
        </Button>
        <Button isLoading={isSubmitting} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}

function CheckTextArea({
  control,
  description,
  label,
  name,
  rows = 2,
}: {
  readonly control: Control<MbcAdminCreateCheckBody>;
  readonly description: string;
  readonly label: string;
  readonly name: "askWhenFailing" | "label" | "passesWhen";
  readonly rows?: number;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField isInvalid={fieldState.invalid} isRequired>
          <Label requiredHint>{label}</Label>
          <TextArea {...field} rows={rows} />
          <Description>{description}</Description>
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}
